import React, { useState } from 'react'

import {
    Button,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
} from '@material-ui/core'

import {
    AddPropertyForm,
    FormStatus,
    RemovePropertyForm,
} from '../../../models/Forms'
import { Container } from '../../../components'

export type TransitionMethod = (
    event: React.MouseEvent<HTMLButtonElement>,
    form: AddPropertyForm | RemovePropertyForm,
    status: FormStatus,
) => void

interface Props {
    form: AddPropertyForm | RemovePropertyForm
    handleTransition: TransitionMethod
    disabled?: boolean
    style?: React.CSSProperties
}

interface FormTransition {
    to_status: FormStatus
    displayString: string
    permission: boolean
}

const getFormTransitions = (form: AddPropertyForm | RemovePropertyForm) => {
    const transitions: {
        positive?: FormTransition
        negative?: FormTransition
        neutral?: FormTransition
    } = {
        positive: undefined,
        negative: undefined,
        neutral: undefined,
    }

    switch (form.status) {
        case FormStatus.PENDING:
            transitions.positive = {
                to_status: FormStatus.COMPLETED,
                displayString: 'Complete',
                permission: true,
            }
            transitions.negative = {
                to_status: FormStatus.CANCELLED,
                displayString: 'Cancel',
                permission: true,
            }
            transitions.neutral = {
                to_status: FormStatus.PAUSE,
                displayString: 'Pause',
                permission: true,
            }
            break
        case FormStatus.PAUSE:
            transitions.positive = {
                to_status: FormStatus.COMPLETED,
                displayString: 'Complete',
                permission: true,
            }
            transitions.negative = {
                to_status: FormStatus.CANCELLED,
                displayString: 'Cancel',
                permission: true,
            }
            transitions.neutral = {
                to_status: FormStatus.PENDING,
                displayString: 'Pending',
                permission: true,
            }
            break
    }

    return transitions
}

export const FormStatusTransitionButtons = (props: Props) => {
    const { form, disabled, style } = props

    const transitions = getFormTransitions(form)
    const theme = useTheme()

    const [showCancelDialog, setShowCancelDialog] = useState(false)

    const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (transitions.negative) {
            props.handleTransition(e, form, transitions.negative.to_status)
            setShowCancelDialog(false)
        }
    }

    return (
        <>
            <Container
                style={{
                    justifyContent: 'flex-start',
                    marginTop: theme.spacing(1),
                    flexDirection: 'row',
                    ...style,
                }}
            >
                {transitions.positive && (
                    <Button
                        color="primary"
                        variant="outlined"
                        disabled={!transitions.positive.permission || disabled}
                        onClick={(e) => {
                            props.handleTransition(
                                e,
                                form,
                                transitions.positive!.to_status,
                            )
                        }}
                    >
                        {transitions.positive.displayString}
                    </Button>
                )}
                {transitions.negative && (
                    <Button
                        color="secondary"
                        variant="outlined"
                        style={{ marginLeft: theme.spacing(1) }}
                        disabled={!transitions.negative.permission || disabled}
                        onClick={() => setShowCancelDialog(true)}
                    >
                        {transitions.negative.displayString}
                    </Button>
                )}
                {transitions.neutral && (
                    <Button
                        color="secondary"
                        variant="outlined"
                        style={{ marginLeft: theme.spacing(1) }}
                        disabled={!transitions.neutral.permission || disabled}
                        onClick={(e) => {
                            props.handleTransition(
                                e,
                                form,
                                transitions.neutral!.to_status,
                            )
                        }}
                    >
                        {transitions.neutral.displayString}
                    </Button>
                )}
            </Container>

            <Dialog
                open={showCancelDialog}
                onClose={() => setShowCancelDialog(false)}
            >
                <DialogTitle>Cancel Form</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to cancel this form? This action
                        cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setShowCancelDialog(false)}
                        color="primary"
                    >
                        No, Keep Form
                    </Button>
                    <Button onClick={handleCancel} color="secondary" autoFocus>
                        Yes, Cancel Form
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
